"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { AnimatePresence, easeOut, motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  AlertCircle,
  Check,
  Eye,
  EyeOff,
  Lock,
  Mail,
  User,
} from "lucide-react";
import { useUpdateCredentials } from "@/features/Auth/AuthApi.ts"; // const Setting =
import { AuthStore } from "@/store/useAuthStore";

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  oldPassword: string;
  password: string;
  confirmPassword: string;
}

export default function Setting() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { userInfo } = AuthStore();

  const { mutateAsync, isPending, isSuccess } = useUpdateCredentials();

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isValid },
    // reset,
  } = useForm<FormData>({
    mode: "onChange",
    defaultValues: {
      firstName: userInfo.firstname || "",
      lastName: userInfo.lastname || "",
      email: userInfo.email || "",
      oldPassword: "",
      password: "",
      confirmPassword: "",
    },
  });

  const password = watch("password");

  const onSubmit = async (values: FormData) => {
    if (values.password !== values.confirmPassword) {
      setError("confirmPassword", {
        type: "manual",
        message: "Passwords do not match",
      });

      return;
    }

    const data = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      newPassword: values.password || undefined,
      oldPassword: values.oldPassword || undefined,
    };

    try {
      await mutateAsync(data);
    } catch {
      // The mutation displays the API error and leaves the form editable.
    }
  };

  const getPasswordStrength = (password: string) => {
    if (!password) return { strength: 0, label: "", color: "" };

    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    const labels = ["Very Weak", "Weak", "Fair", "Good", "Strong"];
    const colors = [
      "bg-red-500",
      "bg-orange-500",
      "bg-yellow-500",
      "bg-blue-500",
      "bg-green-500",
    ];

    return {
      strength,
      label: labels[strength - 1] || "",
      color: colors[strength - 1] || "",
    };
  };

  const passwordStrength = getPasswordStrength(password);

  const inputVariants = {
    focus: {
      scale: 1.02,
      transition: { type: "spring" as const, stiffness: 300, damping: 20 },
    },
    blur: {
      scale: 1,
      transition: { type: "spring" as const, stiffness: 300, damping: 20 },
    },
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: easeOut,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.4, ease: easeOut },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-100/10 via-yellow-100/10 to-slate-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Enhanced animated background elements */}
      <div className="absolute inset-0 overflow-hidden opacity-10">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse animation-delay-2000"></div>
        <div className="absolute top-40 left-40 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-25 animate-pulse animation-delay-4000"></div>
        <div className="absolute top-1/2 right-1/4 w-64 h-64 bg-yellow-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-6000"></div>
        <div className="absolute bottom-1/4 left-1/3 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl opacity-25 animate-pulse animation-delay-8000"></div>
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="w-full max-w-6xl relative z-10"
      >
        <Card className="backdrop-blur-xl bg-white border-gray-400/20 shadow-2xl overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-cyan-500/10"></div>
          <CardHeader className="text-center space-y-1 py-12 relative z-10">
            <motion.div variants={itemVariants} className="">
              <CardTitle className="text-4xl font-bold  bg-clip-text text-gray-600">
                Update Your Credentials
              </CardTitle>
              <CardDescription className="Sparkles text-[#008c8fff] text-lg max-w-2xl mx-auto">
                Enhance your account security with our premium credential
                management system
              </CardDescription>
            </motion.div>
          </CardHeader>

          <CardContent className="px-12 pb-12 relative z-10">
            <AnimatePresence mode="wait">
              {isSuccess ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="text-center py-16"
                >
                  <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl">
                    <Check className="w-12 h-12 text-black" />
                  </div>
                  <h3 className="text-3xl font-semibold text-black mb-4">
                    Success!
                  </h3>
                  <p className="text-gray-300 text-lg">
                    Your credentials have been updated successfully.
                  </p>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={handleSubmit(onSubmit)}
                  className="space-y-8"
                >
                  {/* Personal Information Section */}
                  <motion.div variants={itemVariants} className="space-y-6">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-8 h-8 bg-[#008c8fff] text-white rounded-lg flex items-center justify-center">
                        <User className="w-5 h-5" />
                      </div>
                      <h3 className="text-xl font-semibold text-black">
                        Personal Information
                      </h3>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      <motion.div variants={itemVariants} className="space-y-3">
                        <Label
                          htmlFor="firstName"
                          className="text-[#008c8fff] flex items-center gap-2 text-base font-medium"
                        >
                          First Name
                        </Label>
                        <motion.div
                          variants={inputVariants}
                          whileFocus="focus"
                          // whileBlur="blur"
                        >
                          <Input
                            id="firstName"
                            {...register("firstName", {
                              required: "First name is required",
                              minLength: {
                                value: 2,
                                message: "Minimum 2 characters",
                              },
                            })}
                            className="bg-white/10 border-gray-400/20 text-black placeholder:text-gray-400 focus:border-purple-400 focus:ring-purple-400/20 h-14 text-lg px-6 rounded-xl"
                            placeholder="John"
                          />
                        </motion.div>
                        {errors.firstName && (
                          <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-red-400 text-sm flex items-center gap-2"
                          >
                            <AlertCircle className="w-4 h-4" />
                            {errors.firstName.message}
                          </motion.p>
                        )}
                      </motion.div>

                      <motion.div variants={itemVariants} className="space-y-3">
                        <Label
                          htmlFor="lastName"
                          className="text-[#008c8fff] text-base font-medium"
                        >
                          Last Name
                        </Label>
                        <motion.div
                          variants={inputVariants}
                          whileFocus="focus"
                          // whileBlur="blur"
                        >
                          <Input
                            id="lastName"
                            {...register("lastName", {
                              required: "Last name is required",
                              minLength: {
                                value: 2,
                                message: "Minimum 2 characters",
                              },
                            })}
                            className="bg-white/10 border-gray-400/20 text-black placeholder:text-gray-400 focus:border-purple-400 focus:ring-purple-400/20 h-14 text-lg px-6 rounded-xl"
                            placeholder="Doe"
                          />
                        </motion.div>
                        {errors.lastName && (
                          <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-red-400 text-sm flex items-center gap-2"
                          >
                            <AlertCircle className="w-4 h-4" />
                            {errors.lastName.message}
                          </motion.p>
                        )}
                      </motion.div>
                    </div>
                  </motion.div>

                  {/* Account Security Section */}
                  <motion.div variants={itemVariants} className="space-y-6">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-8 h-8 bg-[#008c8fff] from-purple-500 to-cyan-500 rounded-lg flex items-center justify-center">
                        <Lock className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-[#008c8fff]">
                        Account Security
                      </h3>
                    </div>

                    {/* Email Field */}
                    <motion.div variants={itemVariants} className="space-y-3">
                      <Label
                        htmlFor="email"
                        className="text-[#008c8fff] flex items-center gap-2 text-base font-medium"
                      >
                        <Mail className="w-4 h-4 text-[#008c8fff]" />
                        Email Address
                      </Label>
                      <motion.div
                        variants={inputVariants}
                        whileFocus="focus"
                        // whileBlur="blur"
                      >
                        <Input
                          id="email"
                          type="email"
                          {...register("email", {
                            required: "Email is required",
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: "Invalid email address",
                            },
                          })}
                          className="bg-white/10 border-gray-400/20 text-black placeholder:text-gray-400 focus:border-purple-400 focus:ring-purple-400/20 h-14 text-lg px-6 rounded-xl"
                          placeholder="john@example.com"
                        />
                      </motion.div>
                      {errors.email && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-red-400 text-sm flex items-center gap-2"
                        >
                          <AlertCircle className="w-4 h-4" />
                          {errors.email.message}
                        </motion.p>
                      )}
                    </motion.div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      <motion.div variants={itemVariants} className="space-y-3">
                        <Label
                          htmlFor="oldPassword"
                          className="text-[#008c8fff] text-base font-medium"
                        >
                          Current Password
                        </Label>
                        <Input
                          id="oldPassword"
                          type="password"
                          autoComplete="current-password"
                          {...register("oldPassword", {
                            validate: (value) =>
                              !password ||
                              Boolean(value) ||
                              "Current password is required to change it",
                          })}
                          className="bg-white/10 border-gray-400/20 text-black placeholder:text-gray-400 focus:border-purple-400 focus:ring-purple-400/20 h-14 text-lg px-6 rounded-xl"
                          placeholder="Enter only when changing password"
                        />
                        {errors.oldPassword && (
                          <p className="text-red-400 text-sm flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            {errors.oldPassword.message}
                          </p>
                        )}
                      </motion.div>

                      {/* Password Field */}
                      <motion.div variants={itemVariants} className="space-y-3">
                        <Label
                          htmlFor="password"
                          className="text-black text-base font-medium text-[#008c8fff]"
                        >
                          New Password
                        </Label>
                        <motion.div
                          variants={inputVariants}
                          whileFocus="focus"
                          // whileBlur="blur"
                          className="relative"
                        >
                          <Input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            autoComplete="new-password"
                            {...register("password", {
                              validate: (value) =>
                                !value ||
                                value.length >= 8 ||
                                "Minimum 8 characters",
                            })}
                            className="bg-white/10 border-gray-400/20 text-black placeholder:text-gray-400 focus:border-purple-400 focus:ring-purple-400/20 h-14 text-lg px-6 pr-14 rounded-xl"
                            placeholder="••••••••"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black transition-colors"
                          >
                            {showPassword ? (
                              <EyeOff className="w-5 h-5" />
                            ) : (
                              <Eye className="w-5 h-5" />
                            )}
                          </button>
                        </motion.div>

                        {/* Password Strength Indicator */}
                        {password && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            className="space-y-3"
                          >
                            <div className="flex gap-2">
                              {[...Array(5)].map((_, i) => (
                                <div
                                  key={i}
                                  className={`h-2 flex-1 rounded-full transition-all duration-500 ${
                                    i < passwordStrength.strength
                                      ? passwordStrength.color
                                      : "bg-gray-600"
                                  }`}
                                />
                              ))}
                            </div>
                            <p className="text-sm text-gray-300">
                              Strength:{" "}
                              <span className="font-semibold text-black">
                                {passwordStrength.label}
                              </span>
                            </p>
                          </motion.div>
                        )}

                        {errors.password && (
                          <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-red-400 text-sm flex items-center gap-2"
                          >
                            <AlertCircle className="w-4 h-4" />
                            {errors.password.message}
                          </motion.p>
                        )}
                      </motion.div>

                      {/* Confirm Password Field */}
                      <motion.div variants={itemVariants} className="space-y-3">
                        <Label
                          htmlFor="confirmPassword"
                          className="text-[#008c8fff] text-base font-medium"
                        >
                          Confirm Password
                        </Label>
                        <motion.div
                          variants={inputVariants}
                          whileFocus="focus"
                          // whileBlur='blur'
                          className="relative"
                        >
                          <Input
                            id="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            autoComplete="new-password"
                            {...register("confirmPassword", {
                              validate: (value) =>
                                !password ||
                                value === password ||
                                "Passwords do not match",
                            })}
                            className="bg-white/10 border-gray-400/20 text-black placeholder:text-gray-400 focus:border-purple-400 focus:ring-purple-400/20 h-14 text-lg px-6 pr-14 rounded-xl"
                            placeholder="••••••••"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black transition-colors"
                          >
                            {showConfirmPassword ? (
                              <EyeOff className="w-5 h-5" />
                            ) : (
                              <Eye className="w-5 h-5" />
                            )}
                          </button>
                        </motion.div>
                        {errors.confirmPassword && (
                          <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-red-400 text-sm flex items-center gap-2"
                          >
                            <AlertCircle className="w-4 h-4" />
                            {errors.confirmPassword.message}
                          </motion.p>
                        )}
                      </motion.div>
                    </div>
                  </motion.div>

                  {/* Submit Button */}
                  <motion.div variants={itemVariants} className="pt-6">
                    <Button
                      type="submit"
                      disabled={!isValid || isPending}
                      className="w-full bg-[#015354ff] text-white font-semibold py-6 text-lg rounded-xl transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-2xl hover:shadow-purple-500/25"
                    >
                      {isPending ? (
                        <div className="flex items-center gap-3">
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{
                              duration: 1,
                              repeat: Number.POSITIVE_INFINITY,
                              ease: "linear",
                            }}
                            className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full"
                          />
                          <span>Updating Credentials...</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <span>Update Credentials</span>
                        </div>
                      )}
                    </Button>
                  </motion.div>
                </motion.form>
              )}
            </AnimatePresence>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
